import React, {useState} from 'react';
import HelpTooltip from '../HelpTooltip';
import OeTip from './OeTip';
import {fmt, fmt0, qtyNum} from './format';
import {deliverableQty, groupOf} from './availability';
import {
    channelLabelOf,
    discountPlan,
    discountPreview,
    effectiveChannel,
    masterboxPlan,
} from './discount';

// Rabatt-Übersicht des Auftrags-Editors (Original `#discbar` /
// `renderGroupBar` 966–1000 samt `discountCards` 1135–1234 und den
// Knopf-Bausteinen `rcTrack`/`rcShowBtn`/`rcApplyBtn`/`rcApplyOff`/`rcOk`
// 1109–1135).
//
// Grundgedanke des Originals: erst prüfen, dann anwenden. Jede Rabattart
// bekommt eine eigene Karte mit Fortschrittsbalken, Stufenmarken und einer
// klaren Aussage, was fehlt — statt einer Liste im engen Modal. Gesetzt wird
// nur die Tabelle (blau markiert); nach weclapp geht das erst mit „Speichern".
//
// Abweichung zur Stufe 2: Der Knopf „✎ von Hand setzen" blendet im Original
// die Positionstabelle ein und aus. Ohne Wizard ist die Tabelle hier ohnehin
// immer sichtbar — der Knopf schaltet deshalb nur die ausführliche Erklärung
// zur lieferbaren Menge zu.

/** Fortschrittsbalken mit Stufenmarken (Original `rcTrack`, 1109). */
function Track({value, tiers}) {
    if (!tiers.length) {
        return null;
    }
    const max = tiers[tiers.length - 1][0] || 1;
    const fill = Math.max(0, Math.min(100, value / max * 100));

    return (
        <div className="rc-track">
            <div className="rc-fill" style={{width: `${fill}%`}}/>
            {tiers.map(([thr, lab], i) => {
                const pos = Math.min(100, thr / max * 100);
                const hit = value >= thr;
                return (
                    <React.Fragment key={i}>
                        <div className={`rc-tick${hit ? ' hit' : ''}`} style={{left: `${pos}%`}}/>
                        <div className={`rc-tlabel${hit ? ' hit' : ''}`} style={{left: `${pos}%`}}>{String(lab)}</div>
                    </React.Fragment>
                );
            })}
        </div>
    );
}

/** „Artikel zeigen ↓" — filtert die Tabelle auf diese Gruppe (Original `rcShowBtn`). */
function ShowBtn({groupKey, why, onShowGroup, onToast}) {
    if (why) {
        return (
            <OeTip tip={why}>
                <button type="button" className="rc-link off" onClick={() => onToast(why)}>Artikel zeigen ↓</button>
            </OeTip>
        );
    }
    return (
        <OeTip tip="Zeigt in der Tabelle nur die Artikel dieser Rabattgruppe (zur Kontrolle). Ändert nichts am Beleg.">
            <button type="button" className="rc-link" onClick={() => onShowGroup(groupKey)}>Artikel zeigen ↓</button>
        </OeTip>
    );
}

/** Anwenden-Knopf einer Karte (Original `rcApplyBtn`). */
function ApplyBtn({label, tip, detail, onClick}) {
    return (
        <OeTip tip={tip} detail={detail}>
            <button type="button" className="rc-apply" onClick={onClick}>{label}</button>
        </OeTip>
    );
}

/**
 * Ausgegrauter Knopf statt gar keinem (Original `rcApplyOff`): der Bearbeiter
 * sieht, was es hier gäbe und warum es gerade nicht geht.
 */
function ApplyOff({label, why, onToast}) {
    return (
        <OeTip tip={why}>
            <button type="button" className="rc-apply off" onClick={() => onToast(why)}>{label}</button>
        </OeTip>
    );
}

function Ok({children}) {
    return <span className="rc-ok">✓ {children}</span>;
}

const SRC_LABEL = {feld: 'Feld', caps: 'Caps', pumpmarker: 'Pumpmarker'};

export default function DiscountBar({
    mode,
    order,
    rows,
    stock,
    pending,
    canEdit,
    locked,
    onApplyAll,
    onApplyGroup,
    onShowGroup,
    onToast,
    discManual,
    onDiscManual,
}) {
    // „✎ von Hand setzen" hält im geführten Ablauf zugleich den Rabatt-Schritt
    // sichtbar und blendet die Tabelle ein (Original `DISC_MANUAL`). Ohne
    // Steuerung von außen bleibt der Schalter lokal.
    const [manualLokal, setManualLokal] = useState(false);
    const manual = onDiscManual ? !!discManual : manualLokal;
    const setManual = onDiscManual || setManualLokal;

    if (mode !== 'order' || !order || !stock.groups || locked) {
        return null;
    }

    const plan = discountPlan(rows, stock, order);
    const mbPlan = masterboxPlan(rows, stock);
    const preview = discountPreview(rows, stock, order, pending.channel);
    const aktuellerKanal = pending.channel || order.salesChannel;
    const erlaubteKanaele = (stock.staffelChannels || []).map((c) => channelLabelOf(c, order)).join(' / ');

    // Hinweis, wenn nicht alle Positionen einer Rabattgruppe voll lieferbar
    // sind — dann weicht der Rabatt vom Bestellwert ab.
    const shortInGroup = rows.some((r) => !r.del && !r.locked && groupOf(r, stock) !== 'none'
        && deliverableQty(r, stock, order) < qtyNum(r.quantity));

    const karten = [];

    for (const p of plan) {
        if (p.channel) {
            // Palettenpreis: Schwelle in Stück, Wirkung über den Vertriebsweg.
            const reached = p.palletHit;
            const chSet = aktuellerKanal === p.channel;
            const gapQty = Math.max(0, p.channelMinQty - p.qty);

            karten.push(
                <div className={`rcard${reached ? ' reached' : ''}`} key={p.key}>
                    <div className="rc-top">
                        <b>{p.label}</b>
                        <span className="rc-type">Palettenpreis</span>
                        <span className={`rc-badge ${reached ? 'on' : 'off'}`}>
                            {reached ? 'Palettenpreis' : 'Standard'}
                        </span>
                    </div>
                    <div className="rc-val">{fmt0(p.qty)} Stück · {p.n} Pos.</div>
                    <Track value={p.qty} tiers={[[p.channelMinQty, 'Palettenpreis']]}/>
                    <div className="rc-foot">
                        {reached ? (
                            <span className="done">
                                ✓ {fmt0(p.qty)} Stück — Palettenpreis ({p.channelLabel}) erreicht.
                            </span>
                        ) : (
                            <>
                                Aktuell Standardpreis ·{' '}
                                <span className="miss">noch {fmt0(gapQty)} Stück</span>
                                {' '}bis Palettenpreis (ab {fmt0(p.channelMinQty)}).
                            </>
                        )}
                    </div>
                    <div className="rc-act">
                        {(reached && !chSet && canEdit) ? (
                            <ApplyBtn
                                label="→ Palettenpreis setzen"
                                tip={`Merkt den Vertriebsweg „${p.channelLabel}" vor. weclapp berechnet damit beim `
                                    + 'Speichern alle nicht-versendeten Positionen neu. Vorgemerkt — gespeichert '
                                    + 'wird erst mit „Speichern".'}
                                onClick={() => onApplyGroup(p.key, `✓ Palettenpreis vorgemerkt (${p.channelLabel})`)}
                            />
                        ) : chSet ? (
                            <Ok>Palettenpreis gesetzt</Ok>
                        ) : (
                            <ApplyOff
                                label="→ Palettenpreis setzen"
                                why={`Noch nicht möglich: es fehlen ${fmt0(gapQty)} Stück bis zum Palettenpreis `
                                    + `(ab ${fmt0(p.channelMinQty)}, aktuell ${fmt0(p.qty)}).`}
                                onToast={onToast}
                            />
                        )}
                        <ShowBtn groupKey={p.key} onShowGroup={onShowGroup} onToast={onToast}/>
                    </div>
                </div>,
            );
            continue;
        }

        if (p.hasTiers && p.channelBlocked) {
            // Der Staffelrabatt gilt nur in bestimmten Vertriebswegen.
            const why = `Gesperrt: beim Vertriebsweg „${channelLabelOf(effectiveChannel(order), order)}" gibt es `
                + `keinen Staffelrabatt — nur bei ${erlaubteKanaele}.`;

            karten.push(
                <div className="rcard na" key={p.key}>
                    <div className="rc-top">
                        <b>{p.label}</b>
                        <span className="rc-type">Staffelrabatt</span>
                        <span className="rc-badge off">gesperrt</span>
                    </div>
                    <div className="rc-val">{fmt(p.sum)} € Warenwert · {p.n} Pos.</div>
                    <div className="rc-foot miss">
                        Kein Staffelrabatt beim Vertriebsweg <b>{channelLabelOf(effectiveChannel(order), order)}</b>
                        {' '}— gilt nur bei {erlaubteKanaele}.
                    </div>
                    <div className="rc-act">
                        <ApplyOff label="Staffelrabatt setzen" why={why} onToast={onToast}/>
                        <ShowBtn groupKey={p.key} onShowGroup={onShowGroup} onToast={onToast}/>
                    </div>
                </div>,
            );
            continue;
        }

        if (p.hasTiers) {
            const reached = p.pct !== null;
            const gapEur = p.next ? Math.max(0, p.next[0] - p.sum) : 0;

            karten.push(
                <div className={`rcard${reached ? ' reached' : ''}`} key={p.key}>
                    <div className="rc-top">
                        <b>{p.label}</b>
                        <span className="rc-type">Staffelrabatt</span>
                        <span className={`rc-badge ${reached ? 'on' : 'off'}`}>{reached ? `${p.pct} %` : '0 %'}</span>
                    </div>
                    <div className="rc-val">{fmt(p.sum)} € Warenwert · {p.n} Pos.</div>
                    <Track value={p.sum} tiers={p.tiers.map(([v, pc]) => [v, `${pc} %`])}/>
                    <div className="rc-foot">
                        {p.next ? (
                            <>
                                Aktuell <b>{reached ? `${p.pct} %` : 'kein Rabatt'}</b> ·{' '}
                                <span className="miss">noch {fmt(gapEur)} €</span> bis {p.next[1]} %.
                            </>
                        ) : (
                            <span className="done">✓ Höchststufe {p.pct} % erreicht.</span>
                        )}
                    </div>
                    <div className="rc-act">
                        {(p.pct !== null && p.toChange > 0 && canEdit) ? (
                            <ApplyBtn
                                label={`✓ ${p.toChange} Pos. auf ${p.pct} % setzen`}
                                tip={`Setzt den Rabatt an ${p.toChange} Position(en) dieser Gruppe auf ${p.pct} %. `
                                    + 'Die Zeilen werden blau markiert — gespeichert wird erst mit „Speichern".'}
                                onClick={() => onApplyGroup(p.key, `✓ ${p.label}: ${p.pct} % gesetzt`)}
                            />
                        ) : (p.pct !== null && p.toChange === 0) ? (
                            <Ok>angewendet</Ok>
                        ) : (
                            <ApplyOff
                                label={`✓ auf ${p.next ? p.next[1] : 0} % setzen`}
                                why={'Noch nicht möglich: die Staffel ist nicht erreicht — es fehlen '
                                    + `${fmt(gapEur)} € Warenwert bis ${p.next ? p.next[1] : 0} % `
                                    + `(aktuell ${fmt(p.sum)} € aus ${p.n} Pos., lieferbare Menge).`}
                                onToast={onToast}
                            />
                        )}
                        <ShowBtn groupKey={p.key} onShowGroup={onShowGroup} onToast={onToast}/>
                    </div>
                </div>,
            );
            continue;
        }

        // Gruppe ohne hinterlegte Staffel (zum Beispiel Flame).
        karten.push(
            <div className="rcard na" key={p.key}>
                <div className="rc-top">
                    <b>{p.label}</b>
                    <span className="rc-type">kein Staffelrabatt</span>
                </div>
                <div className="rc-val">{fmt(p.sum)} € Warenwert · {p.n} Pos.</div>
                <div className="rc-foot muted">Für diese Gruppe ist keine Rabattstaffel hinterlegt.</div>
                <div className="rc-act">
                    <ApplyOff
                        label="Staffelrabatt setzen"
                        why={'Für diese Rabattgruppe ist keine Staffel hinterlegt (Pflege unter „Auftrags-Editor — '
                            + 'Stammdaten") — hier gibt es nichts anzuwenden.'}
                        onToast={onToast}
                    />
                    <ShowBtn groupKey={p.key} onShowGroup={onShowGroup} onToast={onToast}/>
                </div>
            </div>,
        );
    }

    // Masterbox-Rabatt: eine Sammelkarte, gilt für jeden Kunden.
    if (stock.masterboxRule && mbPlan.length) {
        const offen = mbPlan.filter((m) => !m.done).length;
        const pcts = [...new Set(mbPlan.map((m) => m.pct))].sort((a, b) => a - b);

        karten.push(
            <div className={`rcard${offen ? '' : ' reached'}`} key="__mb__">
                <div className="rc-top">
                    <b>Masterbox-Rabatt</b>
                    <span className="rc-type">gilt für jeden Kunden</span>
                    <span className={`rc-badge ${offen ? 'off' : 'on'}`}>
                        {pcts.map((x) => `${fmt(x)} %`).join(' / ')}
                    </span>
                </div>
                <div className="rc-val">
                    {mbPlan.length} Artikel mit Masterbox · {offen ? `${offen} offen` : 'alle gesetzt'}
                </div>
                <div className="rc-mb">
                    {mbPlan.map((m, i) => {
                        const box = `${m.row.articleNumber} · ${fmt0(m.qty)} Stk (MB ${fmt0(m.mb)})`;
                        const tag = m.src && SRC_LABEL[m.src]
                            ? <span className="rc-srctag">{SRC_LABEL[m.src]}</span>
                            : null;
                        return m.done ? (
                            <div key={i}>✓ {box} — angewandt ({fmt(m.pct)} %){tag}</div>
                        ) : (
                            <div key={i}>
                                <b>{fmt0(m.full)} Stk @ {fmt(m.pct)} %</b>
                                {m.needsSplit ? ` + ${fmt0(m.remainder)} @ 0 %` : ''}{' '}
                                <span className="muted">{box}</span>{tag}
                            </div>
                        );
                    })}
                </div>
                <div className="rc-act">
                    {(offen && canEdit) ? (
                        <ApplyBtn
                            label={`✓ Masterbox anwenden (${offen})`}
                            tip={'Setzt den Masterbox-Rabatt auf die Stück in vollen Boxen. Gespeichert wird erst mit „Speichern".'}
                            detail={'Positionen mit Restmenge werden geteilt: volle Boxen mit Rabatt, Restmenge ohne — '
                                + 'die Rest-Zeile erscheint direkt darunter.'}
                            onClick={() => onApplyGroup('__mb__', '✓ Masterbox-Rabatt in der Tabelle gesetzt')}
                        />
                    ) : (
                        <Ok>alle angewendet</Ok>
                    )}
                </div>
            </div>,
        );
    }

    // Rabattgruppen OHNE Position im Auftrag trotzdem zeigen — sonst fehlt eine
    // Karte kommentarlos und niemand weiß, ob die Gruppe vergessen wurde.
    const vorhanden = new Set(plan.map((p) => p.key));
    const leere = [];

    for (const g of (stock.rules || [])) {
        if (vorhanden.has(g.key)) {
            continue;
        }
        const hasTiers = (g.tiers || []).length > 0;
        const type = g.channel ? 'Palettenpreis' : (hasTiers ? 'Staffelrabatt' : 'kein Staffelrabatt');
        const ziel = g.channel
            ? `Palettenpreis ab ${fmt0(g.channelMinQty)} Stück`
            : hasTiers
                ? `Staffel ab ${fmt0(g.tiers[0][0])} € → ${g.tiers[0][1]} %`
                : 'keine Staffel hinterlegt';
        const why = `Keine Position dieser Rabattgruppe im Auftrag — ${ziel}.`;

        leere.push(
            <div className="rcard empty" key={`leer-${g.key}`}>
                <div className="rc-top">
                    <b>{g.label}</b>
                    <span className="rc-type">{type}</span>
                    <span className="rc-badge off">nicht im Auftrag</span>
                </div>
                <div className="rc-val">0 Pos. · keine Artikel dieser Gruppe</div>
                <div className="rc-foot muted">{why}</div>
                <div className="rc-act">
                    <ApplyOff
                        label={g.channel ? '→ Palettenpreis setzen' : 'Staffelrabatt setzen'}
                        why={why}
                        onToast={onToast}
                    />
                    <ShowBtn groupKey={g.key} why={why} onShowGroup={onShowGroup} onToast={onToast}/>
                </div>
            </div>,
        );
    }

    const alle = karten.concat(leere);

    return (
        <div className="fbar" id="oe-discbar" style={{display: 'flex'}}>
            <div style={{width: '100%'}}>
                <div className="lbl" style={{marginBottom: 8}}>
                    Rabatte — Übersicht (prüfen, dann anwenden)
                    <HelpTooltip
                        id="oe-discbar-help"
                        placement="bottom"
                        size={0.7}
                        detail={<>
                            Gerechnet wird mit der <b>lieferbaren</b> Menge. Alles bleibt zunächst in der Tabelle
                            stehen (blau markiert) und geht erst mit „Speichern" nach weclapp.
                        </>}
                    >
                        Je Rabattart eine Karte: erreichter Satz, Abstand zur nächsten Stufe und was ein Klick
                        setzen würde.
                    </HelpTooltip>
                </div>

                <div className="rcards">
                    {alle.length ? alle : (
                        <span className="muted">Keine Positionen mit Rabattgruppe im Auftrag.</span>
                    )}
                </div>

                <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', width: '100%', marginTop: 4}}>
                    {preview.length ? (
                        <>
                            <span className="poinfo" style={{display: 'inline'}}>
                                Anzuwenden: <b>{preview.join(' · ')}</b>
                            </span>
                            <span className="sp"/>
                            {canEdit ? (
                                <OeTip tip={'Setzt alle oben genannten Rabatte auf einmal in der Tabelle (blau markiert) — '
                                    + 'gespeichert wird erst mit „Speichern".'}>
                                    <button type="button" className="actbtn" onClick={onApplyAll}>
                                        % jetzt anwenden
                                    </button>
                                </OeTip>
                            ) : null}
                        </>
                    ) : (
                        <>
                            <span className="poinfo" style={{color: 'var(--ok)', display: 'inline'}}>
                                ✓ Nichts anzuwenden — Staffeln nicht erreicht oder Rabatte schon gesetzt.
                            </span>
                            <span className="sp"/>
                        </>
                    )}

                    <OeTip tip={'Blendet die ausführliche Erklärung zur lieferbaren Menge ein und aus. Die Rabatte '
                        + 'lassen sich jederzeit von Hand in der Spalte „Rabatt %" der Tabelle setzen.'}>
                        <button type="button" className="actbtn" onClick={() => setManual(!manual)}>
                            {manual ? '▲ Tabelle ausblenden' : '✎ von Hand setzen'}
                        </button>
                    </OeTip>
                </div>

                {manual ? (
                    <div style={{width: '100%', marginTop: 6}} className="muted">
                        <b>Wichtig:</b> Die Rabatte berücksichtigen nur die <b>lieferbare Menge</b>
                        {' '}(Bestand geprüft) — Positionen ohne Bestand zählen nicht, teilweise gedeckte nur
                        anteilig.
                        {shortInGroup ? (
                            <span style={{color: 'var(--warn)'}}>
                                {' '}Aktuell sind nicht alle Rabattgruppen-Positionen voll lieferbar.
                            </span>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
}
